import { FC } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import { Degree, useGetCoursePlanQuery } from '@teachur/http-client';
import { DegreePlanProgressBar } from '@new/features/course-builder/components/DegreePlanProgressBar/DegreePlanProgressBar';
import { isPlanComplete } from '@new/features/course-builder/lib/planView';
import { useUnifiedCredits } from '@new/features/degree/hooks/useUnifiedCredits';
import { getPlanCreditSegments } from '../../lib/courseSnapshot';
import { NoPlanCreditsSummary } from './NoPlanCreditsSummary';

interface DegreeCreditsBreakdownProps {
  degree: Pick<Degree, 'id' | 'type'>;
}

const COURSE_BUILDER_ROUTE = '/course-builder';

const SeeMyPlanLink: FC = () => (
  <Link
    to={COURSE_BUILDER_ROUTE}
    className="inline-flex items-center gap-1.5 [font-size:var(--fs-l5)]! bold text-(--primary-O900)! hover:underline"
  >
    See my plan
    <ArrowRight aria-hidden className="size-4" />
  </Link>
);

export const DegreeCreditsBreakdown: FC<DegreeCreditsBreakdownProps> = ({ degree }) => {
  const { data: coursePlan, isLoading } = useGetCoursePlanQuery();
  const credits = useUnifiedCredits(degree.type);

  if (isLoading) {
    return null;
  }

  const plan = coursePlan?.plans.find((savedPlan) => savedPlan.degree.id === degree.id);

  if (!plan) {
    return (
      <NoPlanCreditsSummary
        transferredCredits={credits.transfer.total}
        newlaneCredits={credits.earned.total}
      />
    );
  }

  if (isPlanComplete(plan.groups)) {
    return null;
  }

  const segments = getPlanCreditSegments(plan);
  const required = segments.transferred + segments.newlane + segments.remaining;
  const earnedTotal = segments.transferred + segments.newlane;

  return (
    <div className="flex flex-col gap-3 border-b border-(--neutral-black-NB200) bg-(--primary-O100) px-5 py-5 sm:px-7">
      <div className="flex items-center justify-between text-(--neutral-black-NB700)">
        <span className="l5 medium">Degree progress</span>
        <span className="flex items-baseline gap-1">
          <span className="p3 semibold text-(--neutral-black-NB900)">{earnedTotal}</span>
          <span className="l5 text-(--neutral-black-NB500)">/{required} credits</span>
        </span>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={required}
        aria-valuenow={earnedTotal}
      >
        <DegreePlanProgressBar
          groups={plan.groups}
          showHint={false}
          legendExtra={<SeeMyPlanLink />}
        />
      </div>
    </div>
  );
};
